import { inject, Injectable } from '@angular/core';

import { NgpToastManager } from 'ng-primitives/toast';
import { Subject } from 'rxjs';

import { type ToastNotice, type ToastPort, type ToastShowOptions } from '@core/ports';

import { type IconName } from '@shared/icon';

import { ToastComponent } from './toast.component';
import { type ToastActionEvent, type ToastContext, type ToastVariant } from './toast.types';

type ToastContent = ToastNotice & Pick<ToastContext, 'actionLabel' | 'actionId'>;

@Injectable({ providedIn: 'root' })
export class ToastService implements ToastPort {
  private readonly toastManager = inject(NgpToastManager);

  normal(content: ToastNotice, options: ToastShowOptions): Subject<ToastActionEvent> {
    return this.open('normal', content, options);
  }

  action(content: ToastContent, options: ToastShowOptions): Subject<ToastActionEvent> {
    return this.open('action', content, options);
  }

  alert(content: ToastNotice, options: ToastShowOptions): void {
    this.show('alert', { ...content, suffixIcon: content.suffixIcon ?? 'close' }, options);
  }

  private open(
    variant: ToastVariant,
    content: ToastContent,
    options: ToastShowOptions,
  ): Subject<ToastActionEvent> {
    const events = new Subject<ToastActionEvent>();
    this.show(variant, content, options, events);
    return events;
  }

  private show(
    variant: ToastVariant,
    content: ToastContent,
    options: ToastShowOptions,
    events?: Subject<ToastActionEvent>,
  ): void {
    this.toastManager.show(ToastComponent, {
      context: {
        title: content.title,
        description: content.description,
        prefixIcon: content.prefixIcon as IconName | undefined,
        suffixIcon: content.suffixIcon as IconName | undefined,
        actionLabel: content.actionLabel,
        actionId: content.actionId,
        variant,
        events,
      } satisfies ToastContext,
      duration: options.duration,
    });
  }
}
